import {Injectable, signal} from '@angular/core';
import {ElementSettings, Tunings} from '../types/tunings';
import {TuningsManager} from '../common/elf-store/tunings/tunings.manager';
import {COLOR_FROM_GRADIENT_REGEX} from '../common/utils/validators';
import {ErrorMessage, SuccessMessage} from '../types/toastr-notifications.type';
import {SiteDetailsService} from './site-details.service';
import {SettingsApiService} from './api/settings-api.service';
import {ToastrNotificationsService} from './toastr-notifications.service';
import {ModifyMenuDataAddon} from '../types/product.type';
import {finalize, map} from 'rxjs/operators';
import {AddonManager} from '../common/elf-store/addon/addon.manager';
import {SaveChangesModalComponent} from '../modules/site-details/save-changes-modal/save-changes-modal.component';
import {MatDialog} from '@angular/material/dialog';
import {forkJoin, Observable, Subject} from 'rxjs';
import {ConfirmDialogService} from './confirm-dialog.service';
import {ActivityTrackingService} from '../auth/shared/services/activity-tracking.service';
import {ButtonService} from '../modules/site-details/customize/button-settings/button.service';
import {ApiRequestsDataOfTuningsAndAddons, WidgetTuningsProperty} from '../types/update-tunings-addon';
import {areObjectsEqual} from '../common/utils/helpers';
import {UserPermissions} from '../common/elf-store/user-permissions/user-permissions.store';
import {SiteOverviewPlanService} from './site-overview-plan.service';
import {PaidTuningItem, TuningsUpdatePersiteCreateData} from '../../userway/sdk/UserWayApiClient';
import {AmplitudeService} from './amplitude.service';
import {SaveCustomizationSingleSiteProperties} from './ampli';

const REMEDIATION_PAID_TUNINGS_KEYS = [WidgetTuningsProperty.WidgetLayout];

@Injectable({
  providedIn: 'root',
})
export class SaveTuningsAndAddonsChangesService {
  updatedTunings: Partial<Tunings>;
  updatedAddons: ModifyMenuDataAddon;

  originalTunings: Partial<Tunings>;
  originalAddons: ModifyMenuDataAddon;

  private _showSaveTuningsChangesButton = signal(false);
  private _showSaveAddonsChangesButton = signal(false);

  isGradient: boolean;
  linearGradient = '';

  siteId: number;

  loading: boolean = false;
  isGlobal: boolean = false;

  hasSavePermission: boolean = false;

  confirmDialogEmitter: Subject<boolean> = new Subject<boolean>();
  getAddonEmitter: Subject<boolean> = new Subject<boolean>();

  constructor(
    public buttonService: ButtonService,
    public siteOverviewPlanService: SiteOverviewPlanService,
    private dialog: MatDialog,
    private addonManager: AddonManager,
    private tuningsManager: TuningsManager,
    private ampliService: AmplitudeService,
    private confirmDialog: ConfirmDialogService,
    private siteDetailsService: SiteDetailsService,
    private settingsApiService: SettingsApiService,
    private activityTrackingService: ActivityTrackingService,
    private toastrNotificationService: ToastrNotificationsService,
  ) {}

  get showSaveAddonsChangesButton() {
    return this._showSaveAddonsChangesButton();
  }

  get showSaveTuningsChangesButton() {
    return this._showSaveTuningsChangesButton();
  }

  set showSaveAddonsChangesButton(value: boolean) {
    this._showSaveAddonsChangesButton.set(value && this.shouldShowSaveChanges);
  }

  set showSaveTuningsChangesButton(value: boolean) {
    this._showSaveTuningsChangesButton.set(value);
  }

  get showSaveButtonTuningsAndAddons(): boolean {
    return (this.showSaveAddonsChangesButton || this.showSaveTuningsChangesButton) && this.shouldShowSaveChanges;
  }

  get shouldShowSaveChanges() {
    return this.siteDetailsService.selectedTab$.value.id === 'customize';
  }

  get saveButtonLoaderStatus(): {showLoaderSaveAll: boolean; showLoaderSave: boolean} {
    return {showLoaderSaveAll: this.loading && this.isGlobal, showLoaderSave: this.loading && !this.isGlobal};
  }

  get UserPermissions() {
    return UserPermissions;
  }

  // TODO rewrite logic for updated data to use DirtyCheckPlugin in Akita(and only one data in store)
  initUpdatedTuningsData(tunings: Partial<Tunings>): void {
    this.updatedTunings = tunings;
  }

  // TODO rewrite logic for updated data to use DirtyCheckPlugin in Akita(and only one data in store)
  initUpdatedAddonsData(addons: ModifyMenuDataAddon): void {
    this.updatedAddons = addons;
  }

  initOriginalTuningsData(tunings: Partial<Tunings>): void {
    this.isGradient = tunings?.widget_color_gradient !== 'off';
    this.linearGradient = this.isGradient ? tunings?.widget_color_gradient : null;
    this.originalTunings = tunings;
  }

  initOriginalAddonsData(addons: ModifyMenuDataAddon): void {
    this.originalAddons = addons;
  }

  updateTunings(tuningsProperties: Partial<Tunings>): void {
    this.updatedTunings = {...this.updatedTunings, ...tuningsProperties};
    this.tuningsManager.setUpdatedTunings(this.updatedTunings);
    this.showSaveTuningsChangesButton = !areObjectsEqual(
      this.updatedTunings,
      this.updateOriginalTuningsBasedOnButtonGradient(),
    );
  }

  updateAddons(addons: Partial<ModifyMenuDataAddon>): void {
    this.updatedAddons = {...this.updatedAddons, ...addons};
    this.addonManager.setUpdatedModifyMenuData(this.updatedAddons);
    this.showSaveAddonsChangesButton = !areObjectsEqual(this.updatedAddons, this.originalAddons);
  }

  updateOriginalTuningsBasedOnButtonGradient(): Partial<Tunings> {
    const tunings = {...this.originalTunings};
    if (this.isGradient && this.updatedTunings.widget_color_gradient !== this.linearGradient) {
      tunings[WidgetTuningsProperty.WidgetColorGradient] = this.getDefaultLastGradientColor().toUpperCase();
    }
    return tunings;
  }

  getDefaultLastGradientColor(): string {
    const colorsArr = this.originalTunings[WidgetTuningsProperty.WidgetColorGradient].match(COLOR_FROM_GRADIENT_REGEX);
    return colorsArr[colorsArr.length - 1];
  }

  saveUpdatedTuningsAddonsDataAllSites(): void {
    if (this.loading) return;

    this.confirmDialog.openDialog(() => this.saveUpdatedTuningsAddonsData(true));
  }

  saveUpdatedTuningsAddonsData(isGlobal: boolean = false): void {
    if (this.loading) return;
    this.isGlobal = isGlobal;
    const requests: Observable<ApiRequestsDataOfTuningsAndAddons>[] = [];

    if (this.showSaveAddonsChangesButton) {
      requests.push(
        this.saveModifyMenuSettings(isGlobal).pipe(map(response => ({type: 'ModifyMenuSettings', response}))),
      );
    }

    if (this.showSaveTuningsChangesButton) {
      requests.push(
        this.saveTunings(isGlobal).pipe(
          map(response => ({
            type: 'Tunings',
            response,
          })),
        ),
      );
    }
    this.loading = true;
    forkJoin(requests)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: responses => {
          this.setActivityTracking(isGlobal);
          this.handleSuccess(responses);
        },
        error: error => {
          if (error?.response?.status === 403) return;
          this.toastrNotificationService.notifyError({error, message: ErrorMessage.SomethingWrong});
        },
      });
  }

  handleSuccess(responses: ApiRequestsDataOfTuningsAndAddons[]): void {
    responses.forEach(({type, response}) => {
      switch (type) {
        case 'ModifyMenuSettings':
          this.addonManager.setModifyMenuData(this.updatedAddons);
          this.showSaveAddonsChangesButton = false;
          break;
        case 'Tunings':
          this.siteDetailsService.initTunings();
          this.showSaveTuningsChangesButton = false;
          break;
      }
      this.toastrNotificationService.notifySuccess(SuccessMessage.Applied);
      this.confirmDialogEmitter.next(true);
    });
  }

  setActivityTracking(isGlobal: boolean): void {
    const parametersForTracking: SaveCustomizationSingleSiteProperties = {};
    const updatedParameters = [...this.mapTuningsForSaving().map(tuning => tuning.name)];
    updatedParameters.forEach((item: string) => {
      if (item === WidgetTuningsProperty.WidgetLstButtonEnabled) {
        const liveTranslationBtnState = this.updatedTunings[WidgetTuningsProperty.WidgetLstButtonEnabled]
          ? 'enabled'
          : 'disabled';
        this.activityTrackingService.liveTranslationBtnChangeStateEvent(liveTranslationBtnState);
        parametersForTracking.isLstEnabled = this.updatedTunings[WidgetTuningsProperty.WidgetLstButtonEnabled];
      }
      if (item === WidgetTuningsProperty.WidgetLayout) {
        parametersForTracking.widgetType = this.updatedTunings.widget_layout;

        // events to BigQuery
        if (isGlobal) {
          this.activityTrackingService.applyTypeAllSitesEvent();
        } else {
          this.activityTrackingService.applyTypeEvent();
        }
      }
    });

    // events to Amplitude
    if (Object.keys(parametersForTracking).length) {
      if (isGlobal) {
        this.ampliService.saveCustomizationAllSites(parametersForTracking);
      } else {
        this.ampliService.saveCustomizationSingleSite(parametersForTracking);
      }
    }
  }

  saveTunings(isGlobal: boolean): Observable<TuningsUpdatePersiteCreateData> {
    const remediationPaidTunings = [];
    const generalTunings = [];

    this.mapTuningsForSaving().forEach(tuning =>
      REMEDIATION_PAID_TUNINGS_KEYS.includes(tuning.name as WidgetTuningsProperty)
        ? remediationPaidTunings.push(tuning)
        : generalTunings.push(tuning),
    );

    if (!this.siteOverviewPlanService.isFree(this.siteDetailsService.currentSite) && remediationPaidTunings.length) {
      this.settingsApiService.updateSettingsPersitePaid({
        global: isGlobal,
        items: this.mapTuningsForSaving() as PaidTuningItem[],
        siteId: this.siteId,
      });
    }

    return this.settingsApiService.updateSettingsPersite({
      global: isGlobal,
      items: generalTunings,
      host: this.originalTunings.site_name,
    } as ElementSettings);
  }

  saveModifyMenuSettings(isGlobal: boolean): Observable<{code: number}> {
    return this.settingsApiService.updateModifyMenuDetails(this.siteId, {
      serviceType: 'MODIFY_MENU',
      allSites: isGlobal,
      serviceSettings: this.updatedAddons,
    });
  }

  mapTuningsForSaving(): {name: string; value: string | boolean}[] {
    let tunings = this.removeEqualTuningsParameters();
    return Object.entries(tunings)
      .filter(([key, value]) => {
        return value !== undefined && value !== null && value !== '';
      })
      .map(([key, value]) => {
        if (
          key === WidgetTuningsProperty.WidgetCustomTriggerEnabled ||
          key === WidgetTuningsProperty.WidgetLstButtonEnabled ||
          key === WidgetTuningsProperty.WidgetLiveLanguageEnabled
        ) {
          value = value ? 'on' : 'off';
        }
        return {name: key, value: value};
      });
  }

  removeEqualTuningsParameters(): Partial<Tunings> {
    const filteredObject: Partial<Tunings> = {};

    for (const key in this.updatedTunings) {
      if (this.updatedTunings[key] !== this.originalTunings?.[key] || this.isTuningParameterException(key)) {
        filteredObject[key] = this.updatedTunings[key];
      }
    }

    return filteredObject;
  }

  isTuningParameterException(key: string) {
    if (
      key === WidgetTuningsProperty.WidgetColor &&
      this.updatedTunings[WidgetTuningsProperty.WidgetColorGradient] !==
        this.originalTunings[WidgetTuningsProperty.WidgetColorGradient]
    )
      return true;

    if (key === WidgetTuningsProperty.WidgetCustomTrigger) return true;
  }

  resetSaveButtonTuningsAndAddons(): void {
    this.showSaveTuningsChangesButton = false;
    this.showSaveAddonsChangesButton = false;
  }

  discard(): void {
    if (this.loading) return;
    this.buttonService.tunings = null;
    this.siteDetailsService.initTunings();
    this.getAddonEmitter.next(true);
    this.resetSaveButtonTuningsAndAddons();
  }

  checkIfHasUnsavedTuningAndAddons(): Observable<boolean> {
    return new Observable<boolean>(observer => {
      if (this.showSaveButtonTuningsAndAddons) {
        const confirmationDialog = this.dialog.open(SaveChangesModalComponent);
        confirmationDialog.afterClosed().subscribe(result => {
          if (result === undefined) {
            observer.next(true);
          } else {
            observer.next(result as boolean);
          }
          observer.complete();
        });
      } else {
        observer.next(false);
        observer.complete();
      }
    });
  }

  updateModifyMenuRegardingAI(featuresPattern: string) {
    this.originalAddons = {...this.originalAddons, features_pattern: featuresPattern};
  }
}
